import Link from "next/link";
import type { ReactNode } from "react";

export interface ButtonProps {
  href: string;
  variant?: "primary" | "secondary" | "inverse" | "secondaryInverse";
  size?: "sm" | "md";
  className?: string;
  onClick?: () => void;
  children: ReactNode;
}

const focus = "focus-visible:outline-2 focus-visible:outline-offset-2";

const variants = {
  primary: `rounded-control bg-brand-600 text-white shadow-xs hover:bg-brand-700 ${focus} focus-visible:outline-brand-600`,
  // "inverse" is a primary button for use on a brand-coloured background.
  inverse: `rounded-control bg-white text-brand-600 shadow-xs hover:bg-brand-50 ${focus} focus-visible:outline-white`,
  secondary: `rounded-control text-neutral-900 hover:text-brand-600 ${focus} focus-visible:outline-brand-600`,
  // Text link for use on a brand-coloured background.
  secondaryInverse: `rounded-control text-white hover:text-brand-100 ${focus} focus-visible:outline-white`,
};

const sizes = {
  sm: "px-3 py-2 text-sm font-semibold",
  md: "px-3.5 py-2.5 text-sm font-semibold",
};

/** Link-styled-as-button. Secondary variants render as a text link with an arrow. */
export default function Button({
  href,
  variant = "primary",
  size = "md",
  className = "",
  onClick,
  children,
}: ButtonProps) {
  const isText = variant === "secondary" || variant === "secondaryInverse";
  const spacing = isText ? "text-sm/6 font-semibold" : sizes[size];
  return (
    <Link href={href} onClick={onClick} className={`inline-block ${spacing} ${variants[variant]} ${className}`}>
      {children}
      {isText ? <span aria-hidden="true"> →</span> : null}
    </Link>
  );
}
